import {
  readBinder,
  movePocket,
  removeAt,
  clearBinder,
  updateCard,
  filledCount,
  BINDER_EVENT,
  BINDER_SIZE,
} from '@theme/binder-store';

/**
 * <s-my-binder> — one 3x3 binder page the shopper fills, rearranges and buys.
 * Used in the binder drawer and on the My Binder page; both stay in sync.
 *
 * Attributes:
 *   id (required: buttons target it with commandfor), currency, locale,
 *   refresh — when present, re-checks each card's price and availability on load.
 * Data: data-count-template ([count]), data-pick-template ([title]),
 *   data-remove-template ([title]), data-empty-template ([index]),
 *   data-hint, data-picked-message, data-moved-message,
 *   data-added-message, data-error-message, data-cart-add-url, data-cart-url (from Liquid routes).
 *
 * Markup contract:
 *   [data-binder-pockets]          — list the nine pockets render into
 *   template[data-binder-card]     — filled pocket <li>: [data-pocket-pick] button,
 *     [data-pocket-image] img, [data-pocket-remove] button, optional [data-pocket-link],
 *     [data-pocket-title], [data-pocket-price], [data-pocket-sold], [data-pocket-dim]
 *   template[data-binder-slot]     — empty pocket <li> with a [data-pocket-pick] button
 *   [data-binder-count], [data-binder-total], [data-binder-hint], [data-binder-status]
 *   [data-binder-filled] shown when any pocket is filled; [data-binder-empty] when none
 *   [data-binder-add-all]          — the add-all button (busy state)
 *   Commands (commandfor = this id): --pick, --remove (button carries data-index),
 *   --add-all, --clear.
 */
class SMyBinder extends HTMLElement {
  #ac;
  #picked = null;
  #dragFrom = null;
  #formatter;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.#formatter = new Intl.NumberFormat(this.getAttribute('locale') || undefined, {
      style: 'currency',
      currency: this.getAttribute('currency') || 'USD',
    });

    this.addEventListener('command', (event) => this.#onCommand(event), opts);
    window.addEventListener(BINDER_EVENT, () => this.#render(), opts);
    window.addEventListener('storage', () => this.#render(), opts);

    const pockets = this.querySelector('[data-binder-pockets]');
    if (pockets) {
      pockets.addEventListener('dragstart', (event) => this.#onDragStart(event), opts);
      pockets.addEventListener('dragover', (event) => this.#onDragOver(event), opts);
      pockets.addEventListener('dragleave', (event) => this.#pocketOf(event.target)?.removeAttribute('data-drop-target'), opts);
      pockets.addEventListener('drop', (event) => this.#onDrop(event), opts);
      pockets.addEventListener('dragend', () => this.#clearDropTargets(), opts);
    }

    this.#render();
    if (this.hasAttribute('refresh')) this.#refreshCards();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #onCommand(event) {
    const index = Number(event.source?.dataset?.index ?? -1);
    switch (event.command) {
      case '--pick':
        event.preventDefault();
        this.#pick(index);
        break;
      case '--remove':
        event.preventDefault();
        this.#picked = null;
        removeAt(index);
        this.#focusPocket(index);
        break;
      case '--add-all':
        event.preventDefault();
        this.#addAll();
        break;
      case '--clear':
        event.preventDefault();
        this.#picked = null;
        clearBinder();
        break;
    }
  }

  /** Tap one card, then any pocket, to move or swap it. Works for touch, mouse and keyboard. */
  #pick(index) {
    if (index < 0) return;
    const slots = readBinder();
    if (this.#picked === null) {
      if (!slots[index]) return;
      this.#picked = index;
      this.#render();
      this.#setHint(this.dataset.pickedMessage);
      this.#focusPocket(index);
      return;
    }
    const from = this.#picked;
    this.#picked = null;
    if (from !== index) {
      movePocket(from, index);
      this.#announce(this.dataset.movedMessage);
    } else {
      this.#render();
    }
    this.#focusPocket(index);
  }

  #onDragStart(event) {
    const pocket = this.#pocketOf(event.target);
    if (!pocket || !pocket.hasAttribute('data-filled')) return;
    this.#dragFrom = Number(pocket.dataset.index);
    event.dataTransfer?.setData('text/plain', String(this.#dragFrom));
    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
    pocket.setAttribute('data-dragging', '');
  }

  #onDragOver(event) {
    if (this.#dragFrom === null) return;
    const pocket = this.#pocketOf(event.target);
    if (!pocket) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
    this.#clearDropTargets(pocket);
    pocket.setAttribute('data-drop-target', '');
  }

  #onDrop(event) {
    const pocket = this.#pocketOf(event.target);
    if (this.#dragFrom === null || !pocket) return;
    event.preventDefault();
    const from = this.#dragFrom;
    const to = Number(pocket.dataset.index);
    this.#dragFrom = null;
    this.#picked = null;
    this.#clearDropTargets();
    if (from !== to) {
      movePocket(from, to);
      this.#announce(this.dataset.movedMessage);
    }
  }

  #clearDropTargets(except) {
    for (const el of this.querySelectorAll('[data-drop-target],[data-dragging]')) {
      if (el === except) continue;
      el.removeAttribute('data-drop-target');
      if (!except) el.removeAttribute('data-dragging');
    }
    if (!except) this.#dragFrom = null;
  }

  #pocketOf(target) {
    return target instanceof Element ? target.closest('[data-binder-pockets] > [data-index]') : null;
  }

  #focusPocket(index) {
    requestAnimationFrame(() => {
      this.querySelector(`[data-binder-pockets] > [data-index="${index}"] [data-pocket-pick]`)?.focus();
    });
  }

  #render() {
    const list = this.querySelector('[data-binder-pockets]');
    const cardTemplate = this.querySelector('template[data-binder-card]');
    const slotTemplate = this.querySelector('template[data-binder-slot]');
    if (!list || !(cardTemplate instanceof HTMLTemplateElement) || !(slotTemplate instanceof HTMLTemplateElement)) return;

    const slots = readBinder();
    const id = this.id;
    const fragment = document.createDocumentFragment();

    slots.forEach((item, index) => {
      const pocket = (item ? cardTemplate : slotTemplate).content.firstElementChild.cloneNode(true);
      pocket.dataset.index = String(index);

      const pick = pocket.querySelector('[data-pocket-pick]');
      if (pick) {
        pick.setAttribute('commandfor', id);
        pick.setAttribute('command', '--pick');
        pick.dataset.index = String(index);
      }

      if (item) {
        pocket.setAttribute('data-filled', '');
        pocket.draggable = true;
        if (this.#picked === index) {
          pocket.setAttribute('data-picked', '');
          pick?.setAttribute('aria-pressed', 'true');
        }
        pick?.setAttribute('aria-label', (this.dataset.pickTemplate ?? '').replace('[title]', item.title));

        const image = pocket.querySelector('[data-pocket-image]');
        if (image) {
          if (item.image) {
            image.src = item.image;
            image.alt = item.title;
          } else {
            image.remove();
          }
        }
        const remove = pocket.querySelector('[data-pocket-remove]');
        if (remove) {
          remove.setAttribute('commandfor', id);
          remove.setAttribute('command', '--remove');
          remove.dataset.index = String(index);
          remove.setAttribute('aria-label', (this.dataset.removeTemplate ?? '').replace('[title]', item.title));
        }
        const link = pocket.querySelector('[data-pocket-link]');
        if (link) link.href = item.url;
        const title = pocket.querySelector('[data-pocket-title]');
        if (title) title.textContent = item.title;
        const price = pocket.querySelector('[data-pocket-price]');
        if (price) price.textContent = this.#formatter.format((item.price ?? 0) / 100);
        if (item.available === false) {
          pocket.querySelector('[data-pocket-sold]')?.removeAttribute('hidden');
          pocket.querySelector('[data-pocket-dim]')?.classList.add('opacity-40', 'grayscale');
        }
      } else {
        if (this.#picked !== null) pocket.setAttribute('data-target-ready', '');
        pick?.setAttribute('aria-label', (this.dataset.emptyTemplate ?? '').replace('[index]', String(index + 1)));
      }
      fragment.append(pocket);
    });

    list.replaceChildren(fragment);

    const count = filledCount(slots);
    const countEl = this.querySelector('[data-binder-count]');
    if (countEl) countEl.textContent = (this.dataset.countTemplate ?? '').replace('[count]', String(count));

    const total = slots.reduce((sum, item) => (item && item.available !== false ? sum + (item.price ?? 0) : sum), 0);
    const totalEl = this.querySelector('[data-binder-total]');
    if (totalEl) totalEl.textContent = this.#formatter.format(total / 100);

    for (const el of this.querySelectorAll('[data-binder-filled]')) el.hidden = count === 0;
    for (const el of this.querySelectorAll('[data-binder-empty]')) el.hidden = count > 0;
    if (this.#picked === null) this.#setHint(this.dataset.hint);
  }

  #setHint(message) {
    const hint = this.querySelector('[data-binder-hint]');
    if (hint && message) hint.textContent = message;
  }

  #announce(message) {
    const status = this.querySelector('[data-binder-status]');
    if (status && message) status.textContent = message;
  }

  /** Re-checks each pocket's price and stock so a claimed card never reaches the cart. */
  async #refreshCards() {
    const root = window.Shopify?.routes?.root ?? '/';
    const signal = this.#ac.signal;
    const items = readBinder().filter(Boolean);
    await Promise.all(
      items.map(async (item) => {
        try {
          const res = await fetch(`${root}products/${encodeURIComponent(item.handle)}.js`, { signal });
          if (!res.ok) return;
          const product = await res.json();
          const variant = product.variants?.find((v) => String(v.id) === String(item.variantId)) ?? product.variants?.[0];
          if (!variant) return;
          const changes = { available: Boolean(variant.available), price: variant.price, variantId: String(variant.id) };
          if (changes.available !== item.available || changes.price !== item.price || changes.variantId !== String(item.variantId)) {
            updateCard(item.handle, changes);
          }
        } catch {
          // Network hiccup: keep what we stored.
        }
      })
    );
  }

  async #addAll() {
    const button = this.querySelector('[data-binder-add-all]');
    if (button?.getAttribute('aria-disabled') === 'true') return;
    const lines = readBinder()
      .filter((item) => item && item.available !== false)
      .map((item) => ({ merchandiseId: String(item.variantId), quantity: 1 }));
    if (!lines.length) return;

    this.#setBusy(button, true);
    try {
      if (window.Shopify?.actions?.updateCart) {
        const result = await window.Shopify.actions.updateCart(
          { lines },
          { event: { context: 'product', detail: { source: 's-my-binder' } } }
        );
        if (result?.userErrors?.length) {
          this.#announce(result.userErrors[0]?.message || this.dataset.errorMessage);
          return;
        }
      } else {
        const res = await fetch(`${this.dataset.cartAddUrl}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: lines.map((line) => ({ id: Number(line.merchandiseId), quantity: 1 })) }),
        });
        if (!res.ok) throw new Error('add failed');
      }
      this.#announce(this.dataset.addedMessage);
      this.closest('dialog')?.close();
      if (window.Shopify?.actions?.openCart) await window.Shopify.actions.openCart();
      else if (this.dataset.cartUrl) window.location.assign(this.dataset.cartUrl);
    } catch {
      this.#announce(this.dataset.errorMessage);
    } finally {
      this.#setBusy(button, false);
    }
  }

  #setBusy(button, busy) {
    if (!(button instanceof HTMLElement)) return;
    const label = button.querySelector('[data-binder-add-label]');
    if (busy) {
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('opacity-50', 'cursor-wait');
      if (label) {
        label.dataset.idle = label.textContent ?? '';
        label.textContent = button.dataset.busyLabel ?? label.textContent;
      }
    } else {
      button.removeAttribute('aria-disabled');
      button.classList.remove('opacity-50', 'cursor-wait');
      if (label && label.dataset.idle) label.textContent = label.dataset.idle;
    }
  }
}

if (!customElements.get('s-my-binder')) customElements.define('s-my-binder', SMyBinder);
